import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Tabs,
  Form,
  Input,
  Select,
  Button,
  Upload,
  Row,
  Col,
  message,
  Space,
  Tooltip,
  ConfigProvider,
  Table,
  Tag,
  Pagination,
  Drawer,
  Spin,
  Empty,
  Modal,
} from "antd";
import {
  InboxOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CloudUploadOutlined,
  FunctionOutlined,
  UnorderedListOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { MakeApiCall } from "../../../apis";

const { TextArea } = Input;
const { Dragger } = Upload;

const CATEGORY_OPTIONS = [
  { label: "FAQ", value: "faq" },
  { label: "Policy", value: "policy" },
  { label: "Formula", value: "formula" },
  { label: "Guide", value: "guide" },
  { label: "Custom", value: "custom" },
];

const slugify = (str = "") =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const parseTags = (val = "") =>
  String(val)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

const sectionCardStyle = {
  borderRadius: 12,
  background: "var(--app-surface)",
  padding: "28px 28px 24px",
  border: "1px solid var(--app-border)",
};

// ─── Section 1: Add Text Knowledge ─────────────────────────────────────────────
const TextKnowledgeForm = ({ userData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [slug, setSlug] = useState("");

  const onTitleChange = (e) => {
    const next = slugify(e.target.value);
    setSlug(next);
    form.setFieldsValue({ slug: next });
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        title: values.title?.trim(),
        slug: slugify(values.title),
        category: values.category,
        content: values.content,
        publish: true,
        tags: parseTags(values.tags),
      };
      const response = await MakeApiCall(
        "admin/ai-chat/knowledge",
        "post",
        payload,
        true,
        {},
        userData?.token
      );
      if (response?.status) {
        message.success(response?.message || "Knowledge saved successfully");
        form.resetFields();
        setSlug("");
      } else {
        message.warning(response?.message || "Failed to save knowledge");
      }
    } catch (err) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={sectionCardStyle}>
      <div className="mb-6">
        <h4 className="fw-bolder text-gray-800 mb-1">Add Text Knowledge</h4>
        <div className="text-muted fs-7">
          Save plain-text knowledge so the AI assistant can reference it.
        </div>
      </div>
      <Form layout="vertical" form={form} onFinish={onFinish} requiredMark>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input
                placeholder="e.g. How to reset password"
                onChange={onTitleChange}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Slug" name="slug">
              <Input
                placeholder="auto-generated-from-title"
                size="large"
                value={slug}
                readOnly
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: "Select a category" }]}
            >
              <Select
                size="large"
                placeholder="Choose category"
                options={CATEGORY_OPTIONS}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Tags"
              name="tags"
              rules={[{ message: "Add at least one tag" }]}
              extra="Comma-separated values, e.g. billing, refund, account"
            >
              <Input size="large" placeholder="tag1, tag2, tag3" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label="Content"
              name="content"
              rules={[{ required: true, message: "Content is required" }]}
              style={{ marginBottom: 32 }}
            >
              <TextArea
                rows={6}
                showCount
                maxLength={5000}
                placeholder="Write the knowledge content the AI should learn..."
              />
            </Form.Item>
          </Col>
        </Row>
        <div className="d-flex justify-content-end mt-4">
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            style={{ minWidth: 180 }}
          >
            Save Knowledge
          </Button>
        </div>
      </Form>
    </div>
  );
};

// ─── Section 2: Upload Document ────────────────────────────────────────────────
const UploadDocumentForm = ({ userData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const onTitleChange = (e) => {
    form.setFieldsValue({ slug: slugify(e.target.value) });
  };

  const beforeUpload = (file) => {
    const isAllowed =
      file.name?.toLowerCase().endsWith(".txt") ||
      file.name?.toLowerCase().endsWith(".md");
    if (!isAllowed) {
      message.error("Only .txt and .md files are allowed");
      return Upload.LIST_IGNORE;
    }
    setFileList([file]);
    return false;
  };

  const onRemove = () => setFileList([]);

  const onFinish = async (values) => {
    if (!fileList.length) {
      message.warning("Please upload a .txt or .md file");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title?.trim());
      formData.append("slug", slugify(values.title));
      formData.append("category", values.category);
      formData.append("publish", "true");
      parseTags(values.tags).forEach((t) => formData.append("tags[]", t));
      formData.append("file", fileList[0]);

      const response = await MakeApiCall(
        "admin/ai-chat/knowledge",
        "post",
        formData,
        true,
        {},
        userData?.token
      );
      if (response?.status) {
        message.success(response?.message || "Document uploaded successfully");
        form.resetFields();
        setFileList([]);
      } else {
        message.warning(response?.message || "Failed to upload document");
      }
    } catch (err) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={sectionCardStyle}>
      <div className="mb-6">
        <h4 className="fw-bolder text-gray-800 mb-1">Upload Document</h4>
        <div className="text-muted fs-7">
          Upload a <code>.txt</code> or <code>.md</code> file to train the AI.
        </div>
      </div>
      <Form layout="vertical" form={form} onFinish={onFinish} requiredMark>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input
                size="large"
                placeholder="e.g. Refund Policy Document"
                onChange={onTitleChange}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Slug" name="slug">
              <Input size="large" placeholder="auto-generated-from-title" readOnly disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: "Select a category" }]}
            >
              <Select
                size="large"
                placeholder="Choose category"
                options={CATEGORY_OPTIONS}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Tags"
              name="tags"
              rules={[{ message: "Add at least one tag" }]}
              extra="Comma-separated values"
            >
              <Input size="large" placeholder="tag1, tag2, tag3" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label="File"
              required
              extra="Only .txt and .md files (max 1 file)"
            >
              <Dragger
                multiple={false}
                maxCount={1}
                accept=".txt,.md"
                beforeUpload={beforeUpload}
                onRemove={onRemove}
                fileList={fileList.map((f) => ({
                  uid: f.uid || "-1",
                  name: f.name,
                  status: "done",
                }))}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: "#f45b68" }} />
                </p>
                <p className="ant-upload-text fw-bold">
                  Click or drag a file to this area to upload
                </p>
                <p className="ant-upload-hint text-muted">
                  Supported formats: .txt, .md
                </p>
              </Dragger>
            </Form.Item>
          </Col>
        </Row>
        <div className="d-flex justify-content-end">
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            style={{ minWidth: 180 }}
          >
            Upload Document
          </Button>
        </div>
      </Form>
    </div>
  );
};

// ─── Section 3: Add Formula ────────────────────────────────────────────────────
const FormulaForm = ({ userData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [variables, setVariables] = useState([{ name: "", description: "" }]);

  const onTitleChange = (e) => {
    form.setFieldsValue({ slug: slugify(e.target.value) });
  };

  const updateVariable = (idx, key, value) => {
    setVariables((prev) =>
      prev.map((v, i) => (i === idx ? { ...v, [key]: value } : v))
    );
  };

  const addVariable = () =>
    setVariables((prev) => [...prev, { name: "", description: "" }]);

  const removeVariable = (idx) =>
    setVariables((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
    );

  const variablesObject = useMemo(() => {
    return variables.reduce((acc, v) => {
      const key = v.name?.trim();
      if (key) acc[key] = v.description?.trim() || "";
      return acc;
    }, {});
  }, [variables]);

  const onFinish = async (values) => {
    const validVars = variables.filter((v) => v.name?.trim());
    if (!validVars.length) {
      message.warning("Add at least one variable");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        slug: slugify(values.title),
        title: values.title?.trim(),
        expression: values.expression?.trim(),
        variables: variablesObject,
        example: values.example?.trim(),
        publish: true,
      };
      const response = await MakeApiCall(
        "admin/ai-chat/knowledge/formula",
        "post",
        payload,
        true,
        {},
        userData?.token
      );
      if (response?.status) {
        message.success(response?.message || "Formula saved successfully");
        form.resetFields();
        setVariables([{ name: "", description: "" }]);
      } else {
        message.warning(response?.message || "Failed to save formula");
      }
    } catch (err) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={sectionCardStyle}>
      <div className="mb-6">
        <h4 className="fw-bolder text-gray-800 mb-1">Add Formula</h4>
        <div className="text-muted fs-7">
          Define a reusable formula with variables and an example.
        </div>
      </div>
      <Form layout="vertical" form={form} onFinish={onFinish} requiredMark>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input
                size="large"
                placeholder="e.g. Profit Margin"
                onChange={onTitleChange}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Slug" name="slug">
              <Input size="large" placeholder="auto-generated-from-title" readOnly disabled />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label="Expression"
              name="expression"
              rules={[{ required: true, message: "Expression is required" }]}
              extra="Mathematical or logical formula, e.g. (revenue - cost) / revenue * 100"
            >
              <Input size="large" placeholder="(revenue - cost) / revenue * 100" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <label className="fw-bold text-gray-800">
                Variables <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addVariable}
                style={{ borderColor: "#f45b68", color: "#f45b68" }}
              >
                Add Variable
              </Button>
            </div>
            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              {variables.map((v, idx) => (
                <Row gutter={12} key={idx} align="middle">
                  <Col xs={24} md={10}>
                    <Input
                      size="large"
                      placeholder="Variable name (e.g. revenue)"
                      value={v.name}
                      onChange={(e) => updateVariable(idx, "name", e.target.value)}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Input
                      size="large"
                      placeholder="Description"
                      value={v.description}
                      onChange={(e) =>
                        updateVariable(idx, "description", e.target.value)
                      }
                    />
                  </Col>
                  <Col xs={24} md={2}>
                    <Tooltip title="Remove variable">
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        size="large"
                        disabled={variables.length === 1}
                        onClick={() => removeVariable(idx)}
                      />
                    </Tooltip>
                  </Col>
                </Row>
              ))}
            </Space>
          </Col>

          <Col xs={24} className="mt-4">
            <Form.Item
              label="Example"
              name="example"
              rules={[{ required: true, message: "Example is required" }]}
            >
              <Input
                size="large"
                placeholder="e.g. (1000 - 600) / 1000 * 100 = 40%"
              />
            </Form.Item>
          </Col>
        </Row>

        <div className="d-flex justify-content-end">
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            style={{ minWidth: 180 }}
          >
            Save Formula
          </Button>
        </div>
      </Form>
    </div>
  );
};

// ─── Section 4: Knowledge List ─────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { label: "Draft", value: "draft" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
];

const STATUS_COLOR = {
  draft: "default",
  published: "success",
  archived: "warning",
};

const CATEGORY_COLOR = {
  faq: "blue",
  policy: "purple",
  formula: "magenta",
  guide: "cyan",
  custom: "gold",
};

// Keenthemes badge variants used in the rest of the project
const STATUS_BADGE = {
  draft: "badge-light-secondary",
  published: "badge-light-success",
  archived: "badge-light-warning",
};

const CATEGORY_BADGE = {
  faq: "badge-light-primary",
  policy: "badge-light-info",
  formula: "badge-light-danger",
  guide: "badge-light-success",
  custom: "badge-light-warning",
};

const SOURCE_FORMAT_LABEL = {
  text: "Text",
  file: "File",
  structured_formula: "Formula",
};

const formatTs = (ts) => {
  if (!ts) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(ts * 1000));
};

const KnowledgeDetailDrawer = ({ open, onClose, id, userData, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !id) return;
    let cancelled = false;
    const fetchDetail = async () => {
      setLoading(true);
      setDetail(null);
      const response = await MakeApiCall(
        `admin/ai-chat/knowledge/${id}`,
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (cancelled) return;
      if (response?.status) {
        setDetail(response?.data);
      } else {
        message.warning(response?.message || "Failed to load document");
      }
      setLoading(false);
    };
    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [open, id, userData?.token]);

  const handleDelete = () => {
    if (!detail?.id) return;
    Modal.confirm({
      className: "train-ai-delete-confirm-modal",
      title: (
        <span className="fw-bolder train-ai-delete-confirm-title">Delete this document?</span>
      ),
      icon: <i className="ki-outline ki-information-5 fs-2 text-danger me-2" />,
      content: (
        <span className="train-ai-delete-confirm-body">
          You are about to delete <b className="train-ai-delete-confirm-name">{detail.title}</b>.
          This action cannot be undone.
        </span>
      ),
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      okButtonProps: { loading: deleting },
      onOk: async () => {
        setDeleting(true);
        const response = await MakeApiCall(
          `admin/ai-chat/knowledge/${detail.id}`,
          "delete",
          null,
          true,
          {},
          userData?.token
        );
        setDeleting(false);
        if (response?.status) {
          message.success(response?.message || "Document deleted");
          onDeleted?.(detail.id);
          onClose?.();
        } else {
          message.warning(response?.message || "Failed to delete document");
          throw new Error("delete failed");
        }
      },
    });
  };

  const InfoRow = ({ label, children }) => (
    <div className="d-flex flex-wrap align-items-start py-3 border-bottom border-gray-200">
      <div
        className="text-muted fw-semibold fs-7"
        style={{ minWidth: 140, flex: "0 0 140px" }}
      >
        {label}
      </div>
      <div className="text-gray-800 fw-bold fs-7" style={{ flex: 1, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={640}
      closable={false}
      bodyStyle={{ background: "var(--app-bg)", padding: 20 }}
      headerStyle={{ display: "none" }}
      destroyOnClose
    >
      {/* Custom header */}
      <div className="card mb-5" style={{ borderRadius: 12 }}>
        <div className="card-body p-5">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
              <div
                className="symbol symbol-50px me-3 flex-shrink-0"
                style={{
                  background: "rgba(244,91,104,0.1)",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="ki-outline ki-book-open fs-2x" style={{ color: "#f45b68" }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 className="fw-bolder text-gray-900 mb-0 text-truncate">
                  {detail?.title || (loading ? "Loading..." : "Knowledge Document")}
                </h3>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              {detail && (
                <button
                  type="button"
                  className="btn btn-sm btn-light-danger d-flex align-items-center"
                  onClick={handleDelete}
                  disabled={deleting}
                  aria-label="Delete"
                >
                  <i className="ki-outline ki-trash fs-3 me-1" />
                  Delete
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light"
                onClick={onClose}
                aria-label="Close"
              >
                <i className="ki-outline ki-cross fs-2" />
              </button>
            </div>
          </div>

          {detail && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              {detail.category && (
                <span
                  className={`badge ${
                    CATEGORY_BADGE[detail.category] || "badge-light"
                  } fw-bold text-capitalize`}
                >
                  <i className="ki-outline ki-tag fs-7 me-1" />
                  {detail.category}
                </span>
              )}
              {detail.status && (
                <span
                  className={`badge ${
                    STATUS_BADGE[detail.status] || "badge-light"
                  } fw-bold text-capitalize`}
                >
                  <i className="ki-outline ki-check-circle fs-7 me-1" />
                  {detail.status}
                </span>
              )}
              {detail.source_format && (
                <span className="badge badge-light-dark fw-bold">
                  <i className="ki-outline ki-document fs-7 me-1" />
                  {SOURCE_FORMAT_LABEL[detail.source_format] || detail.source_format}
                </span>
              )}
              <span className="badge badge-light fw-bold text-gray-700">
                <i className="ki-outline ki-element-plus fs-7 me-1" />
                {detail.chunk_count ?? 0} chunk{(detail.chunk_count ?? 0) === 1 ? "" : "s"}
              </span>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ borderRadius: 12 }}>
          <div className="card-body d-flex justify-content-center py-15">
            <Spin />
          </div>
        </div>
      ) : !detail ? (
        <div className="card" style={{ borderRadius: 12 }}>
          <div className="card-body py-10">
            <Empty description="No data" />
          </div>
        </div>
      ) : (
        <>
          {/* Details card */}
          <div className="card mb-5" style={{ borderRadius: 12 }}>
            <div className="card-header border-bottom-dashed min-h-50px">
              <h3 className="card-title fw-bolder text-gray-800 fs-5">
                <i className="ki-outline ki-information-2 fs-2 me-2 text-danger" />
                Document Details
              </h3>
            </div>
            <div className="card-body py-3 px-5">
              <InfoRow label="ID">#{detail.id}</InfoRow>
              <InfoRow label="Title">{detail.title || "—"}</InfoRow>
              <InfoRow label="Slug">
                <code className="text-gray-700">{detail.slug || "—"}</code>
              </InfoRow>
              <InfoRow label="Category">
                <span className="text-capitalize">{detail.category || "—"}</span>
              </InfoRow>
              <InfoRow label="Status">
                <span className="text-capitalize">{detail.status || "—"}</span>
              </InfoRow>
              <InfoRow label="Source Format">
                {SOURCE_FORMAT_LABEL[detail.source_format] || detail.source_format || "—"}
              </InfoRow>
              <InfoRow label="Tags">
                {detail.tags?.length ? (
                  <div className="d-flex flex-wrap gap-2">
                    {detail.tags.map((t) => (
                      <span
                        key={t}
                        className="badge badge-light-danger fw-bold"
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </InfoRow>
              <InfoRow label="Content Hash">
                <span
                  className="text-muted fs-8 d-inline-block"
                  style={{ wordBreak: "break-all", fontFamily: "monospace" }}
                >
                  {detail.content_hash || "—"}
                </span>
              </InfoRow>
              <InfoRow label="Published At">
                <i className="ki-outline ki-calendar-tick fs-6 me-2 text-success" />
                {formatTs(detail.published_at)}
              </InfoRow>
              <InfoRow label="Created At">
                <i className="ki-outline ki-calendar-add fs-6 me-2 text-primary" />
                {formatTs(detail.created_at)}
              </InfoRow>
              <div className="d-flex flex-wrap align-items-start py-3">
                <div
                  className="text-muted fw-semibold fs-7"
                  style={{ minWidth: 140, flex: "0 0 140px" }}
                >
                  Updated At
                </div>
                <div className="text-gray-800 fw-bold fs-7" style={{ flex: 1, minWidth: 0 }}>
                  <i className="ki-outline ki-calendar-edit fs-6 me-2 text-warning" />
                  {formatTs(detail.updated_at)}
                </div>
              </div>
            </div>
          </div>

          {detail.metadata && (
            <div className="card mb-5" style={{ borderRadius: 12 }}>
              <div className="card-header border-bottom-dashed min-h-50px">
                <h3 className="card-title fw-bolder text-gray-800 fs-5">
                  <i className="ki-outline ki-data fs-2 me-2 text-danger" />
                  Metadata
                </h3>
              </div>
              <div className="card-body p-5">
                <pre
                  style={{
                    background: "var(--app-surface-alt)",
                    padding: 12,
                    borderRadius: 8,
                    fontSize: 12,
                    maxHeight: 240,
                    overflow: "auto",
                    margin: 0,
                    border: "1px solid var(--app-border)",
                  }}
                >
                  {JSON.stringify(detail.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {detail.chunks_preview?.length > 0 && (
            <div className="card" style={{ borderRadius: 12 }}>
              <div className="card-header border-bottom-dashed min-h-50px d-flex align-items-center justify-content-between">
                <h3 className="card-title fw-bolder text-gray-800 fs-5">
                  <i className="ki-outline ki-element-plus fs-2 me-2 text-danger" />
                  Chunks Preview
                </h3>
                <span className="badge badge-light-danger fw-bold">
                  {detail.chunks_preview.length} chunk
                  {detail.chunks_preview.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="card-body p-5">
                {detail.chunks_preview.map((c, idx) => (
                  <div
                    key={`${c.chunk_index}-${idx}`}
                    className="mb-4"
                    style={{
                      border: "1px solid var(--app-border)",
                      borderRadius: 10,
                      padding: 14,
                      background: "var(--app-surface-alt)",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge badge-light-dark fw-bold">
                          # {c.chunk_index ?? idx}
                        </span>
                        {c.data_type && (
                          <span className="badge badge-light-primary fw-bold text-capitalize">
                            {c.data_type}
                          </span>
                        )}
                      </div>
                      {c.embedded_at && (
                        <span className="text-muted fs-8">
                          <i className="ki-outline ki-time fs-7 me-1" />
                          Embedded {formatTs(c.embedded_at)}
                        </span>
                      )}
                    </div>
                    <div
                      className="text-gray-800 fs-7"
                      style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
                    >
                      {c.preview || <span className="text-muted">No preview</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

const KnowledgeListSection = ({ userData }) => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState(undefined);
  const [status, setStatus] = useState(undefined);
  const [selectedId, setSelectedId] = useState(null);

  const fetchList = async (override = {}) => {
    const p = override.page ?? page;
    const pp = override.perPage ?? perPage;
    const c = override.category !== undefined ? override.category : category;
    const s = override.status !== undefined ? override.status : status;

    setLoading(true);
    const url = `admin/ai-chat/knowledge?page=${p}&per_page=${pp}&category=${
      c || ""
    }&status=${s || ""}`;
    const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);
    if (response?.status) {
      setList(response?.data?.items || []);
      setTotal(response?.data?.pagination?.total || 0);
      setPage(response?.data?.pagination?.page || 1);
    } else {
      setList([]);
      setTotal(0);
      message.warning(response?.message || "Failed to load knowledge list");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCategoryChange = (val) => {
    setCategory(val);
    setPage(1);
    fetchList({ page: 1, category: val ?? "" });
  };

  const handleStatusChange = (val) => {
    setStatus(val);
    setPage(1);
    fetchList({ page: 1, status: val ?? "" });
  };

  const handleReset = () => {
    setCategory(undefined);
    setStatus(undefined);
    setPage(1);
    fetchList({ page: 1, category: "", status: "" });
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_, __, idx) => (page - 1) * perPage + idx + 1,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, row) => (
        <div className="d-flex flex-column">
          <span
            className="fw-bolder text-gray-800"
            style={{ color: "#f45b68" }}
          >
            {text || "—"}
          </span>
          <span className="text-muted fs-8">{row?.slug}</span>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      align: "center",
      render: (val) =>
        val ? (
          <Tag color={CATEGORY_COLOR[val] || "default"} style={{ textTransform: "capitalize" }}>
            {val}
          </Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (val) =>
        val ? (
          <Tag color={STATUS_COLOR[val] || "default"} style={{ textTransform: "capitalize" }}>
            {val}
          </Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "Source",
      dataIndex: "source_format",
      key: "source_format",
      align: "center",
      render: (val) => (
        <span className="text-gray-700">
          {SOURCE_FORMAT_LABEL[val] || val || "—"}
        </span>
      ),
    },
    {
      title: "Chunks",
      dataIndex: "chunk_count",
      key: "chunk_count",
      align: "center",
      render: (val) => <span>{val ?? 0}</span>,
    },
    {
      title: "Published At",
      dataIndex: "published_at",
      key: "published_at",
      align: "center",
      render: (val) => <span>{formatTs(val)}</span>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      render: (val) => <span>{formatTs(val)}</span>,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 80,
      render: (_, row) => (
        <Tooltip title="Delete">
          <Button
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              confirmDelete(row);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const confirmDelete = (row) => {
    if (!row?.id) return;
    Modal.confirm({
      className: "train-ai-delete-confirm-modal",
      title: (
        <span className="fw-bolder train-ai-delete-confirm-title">Delete this document?</span>
      ),
      icon: <i className="ki-outline ki-information-5 fs-2 text-danger me-2" />,
      content: (
        <span className="train-ai-delete-confirm-body">
          You are about to delete <b className="train-ai-delete-confirm-name">{row.title}</b>.
          This action cannot be undone.
        </span>
      ),
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        const response = await MakeApiCall(
          `admin/ai-chat/knowledge/${row.id}`,
          "delete",
          null,
          true,
          {},
          userData?.token
        );
        if (response?.status) {
          message.success(response?.message || "Document deleted");
          // Refresh; if last item on page got deleted, go back a page
          const isLastOnPage = list.length === 1 && page > 1;
          const nextPage = isLastOnPage ? page - 1 : page;
          setPage(nextPage);
          fetchList({ page: nextPage });
        } else {
          message.warning(response?.message || "Failed to delete document");
          throw new Error("delete failed");
        }
      },
    });
  };

  return (
    <div style={sectionCardStyle}>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-6">
        <div>
          <h4 className="fw-bolder text-gray-800 mb-1">Knowledge Base</h4>
          <div className="text-muted fs-7">
            Browse all knowledge entries the AI assistant can reference.
          </div>
        </div>
        <Space wrap>
          <Select
            allowClear
            placeholder="All categories"
            size="large"
            style={{ minWidth: 180 }}
            value={category}
            onChange={handleCategoryChange}
            options={CATEGORY_OPTIONS}
          />
          <Select
            allowClear
            placeholder="All statuses"
            size="large"
            style={{ minWidth: 170 }}
            value={status}
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
          />
          <Tooltip title="Reset & refresh">
            <Button
              size="large"
              icon={<ReloadOutlined />}
              onClick={handleReset}
            />
          </Tooltip>
        </Space>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        pagination={false}
        scroll={{ x: "max-content" }}
        onRow={(record) => ({
          onClick: () => setSelectedId(record?.id),
          style: { cursor: "pointer" },
        })}
      />

      <div className="d-flex justify-content-end mt-5">
        <Pagination
          current={page}
          pageSize={perPage}
          total={total}
          showSizeChanger
          pageSizeOptions={[10, 20, 50, 100]}
          onChange={(p, pp) => {
            setPage(p);
            setPerPage(pp);
            fetchList({ page: p, perPage: pp });
          }}
          showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} items`}
        />
      </div>

      <KnowledgeDetailDrawer
        open={!!selectedId}
        id={selectedId}
        userData={userData}
        onClose={() => setSelectedId(null)}
        onDeleted={() => {
          const isLastOnPage = list.length === 1 && page > 1;
          const nextPage = isLastOnPage ? page - 1 : page;
          setPage(nextPage);
          fetchList({ page: nextPage });
        }}
      />
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────
const THEME = {
  token: {
    colorPrimary: "#f45b68",
    colorLink: "#f45b68",
    colorLinkHover: "#d94655",
    colorInfo: "#f45b68",
  },
  components: {
    Tabs: {
      inkBarColor: "#f45b68",
      itemActiveColor: "#f45b68",
      itemHoverColor: "#f45b68",
      itemSelectedColor: "#f45b68",
    },
    Button: {
      colorPrimary: "#f45b68",
      colorPrimaryHover: "#d94655",
      colorPrimaryActive: "#c43d4d",
    },
  },
};

export default function TrainAI(props) {
  const { userData } = props;

  const tabItems = [
    {
      key: "list",
      label: (
        <span>
          <UnorderedListOutlined /> Knowledge List
        </span>
      ),
      children: <KnowledgeListSection userData={userData} />,
    },
    {
      key: "text",
      label: (
        <span>
          <FileTextOutlined /> Add Text Knowledge
        </span>
      ),
      children: <TextKnowledgeForm userData={userData} />,
    },
    {
      key: "upload",
      label: (
        <span>
          <CloudUploadOutlined /> Upload Document
        </span>
      ),
      children: <UploadDocumentForm userData={userData} />,
    },
    {
      key: "formula",
      label: (
        <span>
          <FunctionOutlined /> Add Formula
        </span>
      ),
      children: <FormulaForm userData={userData} />,
    },
  ];

  return (
    <ConfigProvider theme={THEME}>
      <Helmet>
        <title>Train AI</title>
      </Helmet>

      <div className="train-ai-page">
        <div className="mb-6 mt-2">
          <h2 className="fw-bolder text-gray-900 mb-1">Train AI</h2>
          <div className="text-muted fs-6">
            Add knowledge, upload documents, or define formulas to train the
            chatbot.
          </div>
        </div>

        <div
          className="card card-xxl-stretch mb-5 mb-xl-8 train-ai-tabs-card"
          style={{ borderRadius: 12 }}
        >
          <div className="card-body p-4">
            <Tabs
              defaultActiveKey="list"
              items={tabItems}
              size="large"
              tabBarStyle={{ marginBottom: 24 }}
            />
          </div>
        </div>
      </div>

      <style>{`
        .train-ai-page .train-ai-tabs-card {
          background: var(--app-surface);
          border-color: var(--app-border);
        }
        .train-ai-page .text-gray-900,
        .train-ai-page .text-gray-800 {
          color: var(--app-text) !important;
        }
        .train-ai-page .text-gray-700,
        .train-ai-page .text-muted {
          color: var(--app-text-muted) !important;
        }
        .train-ai-page .train-ai-tabs-card .ant-tabs-tab {
          font-weight: 600;
          color: var(--app-text-muted);
          padding: 12px 18px !important;
          border-radius: 8px 8px 0 0;
          transition: color 0.2s ease, background 0.2s ease;
        }
        .train-ai-page .train-ai-tabs-card .ant-tabs-tab:hover {
          color: #f45b68 !important;
        }
        .train-ai-page .train-ai-tabs-card .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #f45b68 !important;
        }
        .train-ai-page .train-ai-tabs-card .ant-tabs-tab-btn:focus,
        .train-ai-page .train-ai-tabs-card .ant-tabs-tab-btn:active,
        .train-ai-page .train-ai-tabs-card .ant-tabs-tab-remove:focus,
        .train-ai-page .train-ai-tabs-card .ant-tabs-tab-remove:active {
          color: #f45b68 !important;
        }
        .train-ai-page .train-ai-tabs-card .ant-tabs-ink-bar {
          background: #f45b68 !important;
          height: 3px !important;
          border-radius: 3px;
        }
        .train-ai-page .train-ai-tabs-card .ant-tabs-tab .anticon {
          margin-right: 6px;
        }
        .train-ai-page .ant-btn-primary {
          background: #f45b68 !important;
          border-color: #f45b68 !important;
        }
        .train-ai-page .ant-btn-dashed {
          border-color: var(--app-border);
          color: var(--app-text);
        }
        .train-ai-page .ant-upload-wrapper .ant-upload-drag {
          background: var(--app-surface-alt) !important;
          border-color: var(--app-border) !important;
        }
      `}</style>
    </ConfigProvider>
  );
}
